import { useState, useEffect, useCallback } from "react";
import {
  Container,
  Box,
  Typography,
  Button,
  Alert,
  Tab,
  TextField,
  CircularProgress,
  IconButton,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import Grid from "@mui/material/Grid";

import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import CalculateRoundedIcon from "@mui/icons-material/CalculateRounded";
import GavelRoundedIcon from "@mui/icons-material/GavelRounded";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import PercentRoundedIcon from "@mui/icons-material/PercentRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";

import { useTheme } from "@mui/material/styles";
import AppDialog from "../components/General/Dialog/AppDialog";
import ConfirmDialog from "../components/General/Dialog/ConfirmDialog";
import GlassCard from "../components/General/GlassCard";
import ActionHeaderCard from "../components/General/ActionHeaderCard";
import DataGridCustom from "../components/General/DatagridCustom";
import DependencyItemTypesDialog from "../components/Liquidation/catalog/DependencyItemTypesDialog";
import CreateDependencyDialog from "../components/Liquidation/catalog/CreateDependencyDialog";
import { useToast } from "../context/ToastContext";
import { formatCurrency } from "../config/Helper";
import CurrencyInput from "../components/General/CurrencyInput";
import {
  getJusValues,
  createJusValue,
  updateJusValue,
  deleteJusValue,
  getBarAssocValues,
  createBarAssocValue,
  updateBarAssocValue,
  deleteBarAssocValue,
  getBarCollegeFees,
  createBarCollegeFee,
  updateBarCollegeFee,
  deleteBarCollegeFee,
  getJusticeTaxRates,
  createJusticeTaxRate,
  updateJusticeTaxRate,
  deleteJusticeTaxRate,
  getLiquidationItemTypes,
  createLiquidationItemType,
  updateLiquidationItemType,
  deleteLiquidationItemType,
  getDependenciesConfig,
  createDependency,
  updateDependency,
} from "../services/LiquidationService";

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(v) {
  if (!v) return "—";
  return new Date(v).toLocaleDateString("es-AR");
}

const CATEGORY_OPTIONS = ["capital", "honorarios", "gastos"];
const CATEGORY_LABELS = {
  capital: "Capital",
  honorarios: "Honorarios",
  gastos: "Gastos",
};
const CALC_TYPE_OPTIONS = [
  { value: "jus_multiplier", label: "JUS (multiplicador)" },
  { value: "percentage_of_amount", label: "Porcentaje del monto" },
  { value: "interest_on_tax", label: "Interés sobre tasa" },
  { value: "fixed_catalog", label: "Catálogo fijo" },
  { value: "manual", label: "Manual" },
];

// ── Date+amount panel (server-paginated) ──────────────────────────────────────

function DateAmountPanel({
  icon,
  title,
  subtitle,
  fetchFn,
  createFn,
  updateFn,
  deleteFn,
  amountLabel = "Monto ($)",
}) {
  const { showToast } = useToast();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [filterModel, setFilterModel] = useState({
    items: [],
    quickFilterValues: [],
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [formDate, setFormDate] = useState(null);
  const [formAmount, setFormAmount] = useState(null);
  const [formNotes, setFormNotes] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const search = filterModel.quickFilterValues?.join(" ") || "";
    try {
      const res = await fetchFn({
        perPage: paginationModel.pageSize,
        page: paginationModel.page + 1,
        search,
      });
      setRows(res.data?.data ?? []);
      setTotal(res.data?.total ?? 0);
    } catch {
      showToast("Error al cargar registros.", "error");
    } finally {
      setLoading(false);
    }
  }, [fetchFn, paginationModel, filterModel, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setFormDate(null);
    setFormAmount(null);
    setFormNotes("");
    setError("");
    setDialogOpen(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setFormDate(row.effective_from ? dayjs(row.effective_from) : null);
    setFormAmount(row.amount ?? null);
    setFormNotes(row.notes ?? "");
    setError("");
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formDate || formAmount == null) {
      setError("Completá la fecha y el monto.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = {
        effective_from: formDate.format("YYYY-MM-DD"),
        amount: formAmount,
        notes: formNotes || null,
      };
      if (editing) await updateFn(editing.id, payload);
      else await createFn(payload);
      setDialogOpen(false);
      load();
      showToast(
        editing ? "Registro actualizado." : "Registro creado.",
        "success",
      );
    } catch (e) {
      setError(e?.response?.data?.message ?? "Error al guardar.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteFn(deleteTarget.id);
      setDeleteTarget(null);
      load();
      showToast("Registro eliminado.", "success");
    } catch {
      showToast("Error al eliminar.", "error");
    }
  };

  const columns = [
    {
      field: "effective_from",
      headerName: "Vigencia",
      flex: 1,
      renderCell: (p) => fmtDate(p.value),
    },
    {
      field: "amount",
      headerName: amountLabel,
      flex: 1,
      align: "right",
      headerAlign: "right",
      renderCell: ({ value }) => formatCurrency(value),
    },
    {
      field: "notes",
      headerName: "Notas",
      flex: 2,
      renderCell: (p) =>
        p.value ? (
          <Typography
            variant="caption"
            sx={{ opacity: 0.7, fontStyle: "italic" }}
          >
            {p.value}
          </Typography>
        ) : null,
    },
    {
      field: "actions",
      headerName: "",
      flex: 0.5,
      sortable: false,
      renderCell: (p) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <IconButton size="small" onClick={() => openEdit(p.row)}>
            <EditRoundedIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => setDeleteTarget(p.row)}
          >
            <DeleteRoundedIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <>
      <DataGridCustom
        rows={rows}
        columns={columns}
        icon={icon}
        title={title}
        description={subtitle}
        loading={loading}
        total={total}
        paginationModel={paginationModel}
        onPaginationChange={setPaginationModel}
        filterModel={filterModel}
        onFilterChange={(m) => {
          setFilterModel(m);
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        filterMode="server"
        isServerPagination
        pageSizeOptions={[5, 10, 25, 50]}
        headerActions={
          <Button
            variant="contained"
            size="small"
            startIcon={<AddRoundedIcon />}
            onClick={openCreate}
            sx={{ borderRadius: 2.5, fontWeight: 900 }}
          >
            Agregar
          </Button>
        }
      />

      <AppDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editing ? "Editar registro" : "Nuevo registro"}
        maxWidth="xs"
        fullWidth
        stickyHeader
        actions={
          <>
            <Button onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              startIcon={
                saving ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SaveRoundedIcon />
                )
              }
              onClick={handleSave}
              disabled={saving}
              sx={{ borderRadius: 2, fontWeight: 900 }}
            >
              Guardar
            </Button>
          </>
        }
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 0.5 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <DatePicker
            label="Fecha de vigencia"
            value={formDate}
            onChange={setFormDate}
            format="DD/MM/YYYY"
            slotProps={{ textField: { size: "small", fullWidth: true } }}
          />
          <CurrencyInput
            label={amountLabel}
            value={formAmount}
            onValueChange={({ floatValue }) =>
              setFormAmount(floatValue ?? null)
            }
            size="small"
            fullWidth
          />
          <TextField
            label="Notas (opcional)"
            value={formNotes}
            onChange={(e) => setFormNotes(e.target.value)}
            size="small"
            multiline
            rows={2}
          />
        </Box>
      </AppDialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Eliminar registro"
        message={`¿Eliminar el registro de ${fmtDate(deleteTarget?.effective_from)}?`}
      />
    </>
  );
}

// ── Tax rates panel (server-paginated) ────────────────────────────────────────

function TaxRatesPanel() {
  const { showToast } = useToast();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [filterModel, setFilterModel] = useState({
    items: [],
    quickFilterValues: [],
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [formYear, setFormYear] = useState("");
  const [formBase, setFormBase] = useState(null);
  const [yearExistsData, setYearExistsData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const search = filterModel.quickFilterValues?.join(" ") || "";
    try {
      const res = await getJusticeTaxRates({
        perPage: paginationModel.pageSize,
        page: paginationModel.page + 1,
        search,
      });
      setRows(res.data?.data ?? []);
      setTotal(res.data?.total ?? 0);
    } catch {
      showToast("Error al cargar.", "error");
    } finally {
      setLoading(false);
    }
  }, [paginationModel, filterModel, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setFormYear("");
    setFormBase(null);
    setError("");
    setYearExistsData(null);
    setDialogOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setFormYear(r.fiscal_year);
    setFormBase(r.base_amount ?? null);
    setError("");
    setYearExistsData(null);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formYear || formBase == null) {
      setError("Completá el año y el monto.");
      return;
    }
    setSaving(true);
    setError("");
    setYearExistsData(null);
    const p = { fiscal_year: parseInt(formYear, 10), base_amount: formBase };
    try {
      if (editing) await updateJusticeTaxRate(editing.id, p);
      else await createJusticeTaxRate(p);
      setDialogOpen(false);
      load();
      showToast(editing ? "Actualizado." : "Creado.", "success");
    } catch (e) {
      if (!editing && e?.response?.status === 409 && e?.response?.data?.year_exists) {
        setYearExistsData(e.response.data);
      } else {
        setError(e?.response?.data?.message ?? "Error al guardar.");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleForceUpdate = async () => {
    if (!yearExistsData) return;
    setSaving(true);
    setError("");
    try {
      await updateJusticeTaxRate(yearExistsData.existing_id, {
        fiscal_year: parseInt(formYear, 10),
        base_amount: formBase,
      });
      setDialogOpen(false);
      setYearExistsData(null);
      load();
      showToast("Tasa actualizada.", "success");
    } catch (e) {
      setError(e?.response?.data?.message ?? "Error al actualizar.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteJusticeTaxRate(deleteTarget.id);
      setDeleteTarget(null);
      load();
      showToast("Eliminado.", "success");
    } catch {
      showToast("Error al eliminar.", "error");
    }
  };

  const columns = [
    {
      field: "fiscal_year",
      headerName: "Año Fiscal",
      flex: 1,
    },
    {
      field: "base_amount",
      headerName: "Monto base",
      flex: 1,
      align: "right",
      headerAlign: "right",
      renderCell: ({ value }) => formatCurrency(value),
    },
    {
      field: "actions",
      headerName: "",
      flex: 0.5,
      sortable: false,
      renderCell: (p) => (
        <>
          <IconButton size="small" onClick={() => openEdit(p.row)}>
            <EditRoundedIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => setDeleteTarget(p.row)}
          >
            <DeleteRoundedIcon fontSize="small" />
          </IconButton>
        </>
      ),
    },
  ];

  return (
    <>
      <DataGridCustom
        rows={rows}
        columns={columns}
        icon={<PercentRoundedIcon />}
        title="Tasas de Justicia"
        description="Monto base por año fiscal para el cálculo de intereses"
        loading={loading}
        total={total}
        paginationModel={paginationModel}
        onPaginationChange={setPaginationModel}
        filterModel={filterModel}
        onFilterChange={(m) => {
          setFilterModel(m);
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        filterMode="server"
        isServerPagination
        pageSizeOptions={[5, 10, 25, 50]}
        headerActions={
          <Button
            variant="contained"
            size="small"
            startIcon={<AddRoundedIcon />}
            onClick={openCreate}
            sx={{ borderRadius: 2.5, fontWeight: 900 }}
          >
            Agregar
          </Button>
        }
      />

      <AppDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editing ? "Editar tasa" : "Nueva tasa"}
        maxWidth="xs"
        fullWidth
        stickyHeader
        actions={
          <>
            <Button onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              startIcon={
                saving ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SaveRoundedIcon />
                )
              }
              onClick={handleSave}
              disabled={saving}
              sx={{ borderRadius: 2, fontWeight: 900 }}
            >
              Guardar
            </Button>
          </>
        }
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 0.5 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {yearExistsData && (
            <Alert
              severity="warning"
              action={
                <Button size="small" color="warning" onClick={handleForceUpdate} disabled={saving}>
                  Actualizar
                </Button>
              }
            >
              {yearExistsData.message}
            </Alert>
          )}
          <TextField
            label="Año fiscal"
            value={formYear}
            onChange={(e) => { setFormYear(e.target.value); setYearExistsData(null); }}
            type="number"
            size="small"
            inputProps={{ min: 2000, max: 2100 }}
          />
          <CurrencyInput
            label="Monto base"
            value={formBase}
            onValueChange={({ floatValue }) => setFormBase(floatValue ?? null)}
            size="small"
            fullWidth
          />
        </Box>
      </AppDialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Eliminar tasa"
        message={`¿Eliminar la tasa del año ${deleteTarget?.fiscal_year}?`}
      />
    </>
  );
}

// ── Item types panel (server-paginated) ───────────────────────────────────────

function ItemTypesPanel() {
  const { showToast } = useToast();
  const { palette } = useTheme();
  const isDark = palette.mode === "dark";

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [filterModel, setFilterModel] = useState({
    items: [],
    quickFilterValues: [],
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const emptyForm = {
    name: "",
    key: "",
    category: "honorarios",
    calculation_type: "jus_multiplier",
    applies_iva: false,
    is_default: true,
    sort_order: "",
  };
  const [form, setForm] = useState(emptyForm);
  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const load = useCallback(async () => {
    setLoading(true);
    const search = filterModel.quickFilterValues?.join(" ") || "";
    try {
      const res = await getLiquidationItemTypes({
        perPage: paginationModel.pageSize,
        page: paginationModel.page + 1,
        search,
      });
      setRows(res.data?.data ?? []);
      setTotal(res.data?.total ?? 0);
    } catch {
      showToast("Error al cargar.", "error");
    } finally {
      setLoading(false);
    }
  }, [paginationModel, filterModel, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setError("");
    setDialogOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      name: r.name,
      key: r.key,
      category: r.category,
      calculation_type: r.calculation_type,
      applies_iva: !!r.applies_iva,
      is_default: !!r.is_default,
      sort_order: r.sort_order ?? "",
    });
    setError("");
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name) {
      setError("El nombre es requerido.");
      return;
    }
    if (!form.key) {
      setError("No se pudo generar una clave válida desde el nombre.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const p = {
        ...form,
        sort_order:
          form.sort_order !== "" ? parseInt(form.sort_order, 10) : null,
      };
      if (editing) await updateLiquidationItemType(editing.id, p);
      else await createLiquidationItemType(p);
      setDialogOpen(false);
      load();
      showToast(editing ? "Actualizado." : "Creado.", "success");
    } catch (e) {
      setError(e?.response?.data?.message ?? "Error al guardar.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteLiquidationItemType(deleteTarget.id);
      setDeleteTarget(null);
      load();
      showToast("Eliminado.", "success");
    } catch {
      showToast("Error al eliminar.", "error");
    }
  };

  const columns = [
    {
      field: "name",
      headerName: "Nombre",
      flex: 1.5,
    },
    {
      field: "category",
      headerName: "Categoría",
      width: 110,
      renderCell: (p) => (
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            px: 1,
            py: 0.3,
            borderRadius: 1,
            background: isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.055)",
            border: isDark
              ? "1px solid rgba(255,255,255,0.1)"
              : "1px solid rgba(0,0,0,0.1)",
            backdropFilter: "blur(8px)",
          }}
        >
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: 0.6,
              textTransform: "uppercase",
              lineHeight: 1,
              opacity: isDark ? 0.85 : 0.7,
            }}
          >
            {CATEGORY_LABELS[p.value] ?? p.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "calculation_type",
      headerName: "Tipo cálculo",
      flex: 1,
      renderCell: (p) => (
        <Typography variant="caption" sx={{ opacity: 0.75, fontSize: 11 }}>
          {CALC_TYPE_OPTIONS.find((c) => c.value === p.value)?.label ?? p.value}
        </Typography>
      ),
    },
    {
      field: "applies_iva",
      headerName: "IVA",
      width: 0.5,
      renderCell: (p) =>
        p.value ? (
          <Box
            sx={{
              display: "inline-flex",
              px: 1,
              py: 0.25,
              borderRadius: 1,
              bgcolor: "rgba(156,39,176,0.08)",
              border: "1px solid rgba(156,39,176,0.2)",
            }}
          >
            <Typography
              sx={{ fontSize: 10, fontWeight: 700, color: "secondary.main" }}
            >
              SÍ
            </Typography>
          </Box>
        ) : null,
    },
    {
      field: "is_default",
      headerName: "Defecto",
      width: 0.5,
      renderCell: (p) =>
        p.value ? (
          <Box
            sx={{
              display: "inline-flex",
              px: 1,
              py: 0.25,
              borderRadius: 1,
              bgcolor: "rgba(25,118,210,0.07)",
              border: "1px solid rgba(25,118,210,0.18)",
            }}
          >
            <Typography
              sx={{ fontSize: 10, fontWeight: 700, color: "primary.main" }}
            >
              SÍ
            </Typography>
          </Box>
        ) : null,
    },
    {
      field: "actions",
      headerName: "",
      flex: 0.5,
      sortable: false,
      renderCell: (p) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <IconButton size="small" onClick={() => openEdit(p.row)}>
            <EditRoundedIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => setDeleteTarget(p.row)}
          >
            <DeleteRoundedIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <>
      <DataGridCustom
        rows={rows}
        columns={columns}
        icon={<CategoryRoundedIcon />}
        title="Tipos de Rubros"
        description="Define los conceptos que aparecen en la planilla de liquidación"
        loading={loading}
        total={total}
        paginationModel={paginationModel}
        onPaginationChange={setPaginationModel}
        filterModel={filterModel}
        onFilterChange={(m) => {
          setFilterModel(m);
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        filterMode="server"
        isServerPagination
        pageSizeOptions={[5, 10, 25, 50]}
        headerActions={
          <Button
            variant="contained"
            size="small"
            startIcon={<AddRoundedIcon />}
            onClick={openCreate}
            sx={{ borderRadius: 2.5, fontWeight: 900 }}
          >
            Agregar
          </Button>
        }
      />

      <AppDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editing ? "Editar rubro" : "Nuevo rubro"}
        maxWidth="sm"
        fullWidth
        stickyHeader
        actions={
          <>
            <Button onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              startIcon={
                saving ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SaveRoundedIcon />
                )
              }
              onClick={handleSave}
              disabled={saving}
              sx={{ borderRadius: 2, fontWeight: 900 }}
            >
              Guardar
            </Button>
          </>
        }
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 0.5 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 8 }}>
              <TextField
                fullWidth
                label="Nombre"
                value={form.name}
                onChange={(e) => {
                  const name = e.target.value;
                  setF("name", name);
                  if (!editing) {
                    const key = name
                      .toLowerCase()
                      .normalize("NFD")
                      .replace(/[̀-ͯ]/g, "")
                      .replace(/[^a-z0-9]+/g, "_")
                      .replace(/^_+|_+$/g, "");
                    setF("key", key);
                  }
                }}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                label="Orden"
                value={form.sort_order}
                onChange={(e) => setF("sort_order", e.target.value)}
                size="small"
                type="number"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Categoría</InputLabel>
                <Select
                  value={form.category}
                  label="Categoría"
                  onChange={(e) => setF("category", e.target.value)}
                >
                  {CATEGORY_OPTIONS.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c.charAt(0).toUpperCase() + c.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo de cálculo</InputLabel>
                <Select
                  value={form.calculation_type}
                  label="Tipo de cálculo"
                  onChange={(e) => setF("calculation_type", e.target.value)}
                >
                  {CALC_TYPE_OPTIONS.map((c) => (
                    <MenuItem key={c.value} value={c.value}>
                      {c.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.applies_iva}
                    onChange={(e) => setF("applies_iva", e.target.checked)}
                  />
                }
                label="Aplica IVA (21%)"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.is_default}
                    onChange={(e) => setF("is_default", e.target.checked)}
                  />
                }
                label="Incluir por defecto"
              />
            </Grid>
          </Grid>
        </Box>
      </AppDialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Eliminar rubro"
        message={`¿Eliminar el rubro "${deleteTarget?.name}"?`}
      />
    </>
  );
}

// ── Dependencies panel (paginado con DataGridCustom) ──────────────────────────

function DependenciesPanel() {
  const { showToast } = useToast();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 15,
  });
  const [filterModel, setFilterModel] = useState({
    items: [],
    quickFilterValues: [],
  });
  const [selected, setSelected] = useState(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [renameSaving, setRenameSaving] = useState(false);
  const [renameError, setRenameError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const search = filterModel.quickFilterValues?.join(" ") || "";
      const res = await getDependenciesConfig({
        page: paginationModel.page + 1,
        per_page: paginationModel.pageSize,
        ...(search && { search }),
      });
      setRows(res.data?.data ?? []);
      setTotal(res.data?.total ?? 0);
    } catch {
      showToast("Error al cargar juzgados.", "error");
    } finally {
      setLoading(false);
    }
  }, [paginationModel, filterModel, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const openRename = (row) => {
    setRenameTarget(row);
    setRenameValue(row.name ?? "");
    setRenameError("");
  };

  const handleRenameSave = async () => {
    if (!renameValue.trim()) {
      setRenameError("Completá el nombre.");
      return;
    }
    setRenameSaving(true);
    setRenameError("");
    try {
      await updateDependency(renameTarget.id, { name: renameValue.trim() });
      setRenameTarget(null);
      load();
      showToast("Juzgado renombrado.", "success");
    } catch (e) {
      setRenameError(
        e?.response?.data?.message ?? "Error al renombrar el juzgado.",
      );
    } finally {
      setRenameSaving(false);
    }
  };

  const columns = [
    {
      field: "name",
      headerName: "Juzgado",
      flex: 2,
    },
    {
      field: "liquidation_item_types",
      headerName: "Rubros configurados",
      flex: 1.5,
      sortable: false,
      renderCell: (p) => {
        const count = Array.isArray(p.row.liquidation_item_types)
          ? p.row.liquidation_item_types.length
          : 0;
        return count > 0 ? (
          <Typography
            variant="caption"
            sx={{ color: "success.main", fontWeight: 700 }}
          >
            {count} rubro{count !== 1 ? "s" : ""} configurado
            {count !== 1 ? "s" : ""}
          </Typography>
        ) : (
          <Typography variant="caption" sx={{ opacity: 0.5 }}>
            Por defecto
          </Typography>
        );
      },
    },
    {
      field: "actions",
      headerName: "",
      flex: 0.7,
      sortable: false,
      renderCell: (p) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <IconButton size="small" onClick={() => openRename(p.row)}>
            <EditRoundedIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="primary"
            onClick={() => {
              setSelected(p.row);
              setConfigOpen(true);
            }}
          >
            <TuneRoundedIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <>
      <DataGridCustom
        rows={rows}
        columns={columns}
        icon={<GavelRoundedIcon />}
        title="Configuración por Juzgado"
        description="Asigná rubros y multiplicadores JUS específicos para cada juzgado"
        loading={loading}
        total={total}
        paginationModel={paginationModel}
        onPaginationChange={setPaginationModel}
        filterModel={filterModel}
        onFilterChange={(m) => {
          setFilterModel(m);
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        filterMode="server"
        isServerPagination
        pageSizeOptions={[10, 15, 25, 50]}
        headerActions={
          <Button
            variant="contained"
            size="small"
            startIcon={<AddRoundedIcon />}
            onClick={() => setCreateOpen(true)}
            sx={{ borderRadius: 2.5, fontWeight: 900 }}
          >
            Agregar
          </Button>
        }
      />

      <CreateDependencyDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={createDependency}
        onCreated={() => {
          load();
          showToast("Juzgado creado.", "success");
        }}
      />

      <DependencyItemTypesDialog
        open={configOpen}
        onClose={() => setConfigOpen(false)}
        dependency={selected}
        onSaved={() => {
          load();
          showToast("Configuración guardada.", "success");
        }}
      />

      <AppDialog
        open={!!renameTarget}
        onClose={() => setRenameTarget(null)}
        title="Renombrar juzgado"
        maxWidth="xs"
        fullWidth
        stickyHeader
        actions={
          <>
            <Button onClick={() => setRenameTarget(null)} disabled={renameSaving}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              startIcon={
                renameSaving ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SaveRoundedIcon />
                )
              }
              onClick={handleRenameSave}
              disabled={renameSaving}
              sx={{ borderRadius: 2, fontWeight: 900 }}
            >
              Guardar
            </Button>
          </>
        }
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 0.5 }}>
          {renameError && <Alert severity="error">{renameError}</Alert>}
          <TextField
            label="Nombre del juzgado"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            size="small"
            fullWidth
            autoFocus
          />
        </Box>
      </AppDialog>
    </>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function SettingsLiquidation() {
  // Orden: primero los ABM comunes con Individual (mismo orden), después los propios.
  const [tab, setTab] = useState("5");

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <ActionHeaderCard
        icon={<CalculateRoundedIcon />}
        title="Configuración de Liquidaciones — Municipalidad"
        description="Catálogos y parámetros del módulo de liquidaciones de municipalidad."
        sx={{ mb: 2 }}
      />

      <GlassCard sx={{ p: 0 }}>
        <TabContext value={tab}>
          <TabList
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons={false}
            sx={{
              px: 0.5,
              borderBottom: 1,
              borderColor: "divider",
              "& .MuiTab-root": {
                minHeight: 50,
                textTransform: "none",
                fontWeight: 900,
                px: 1.2,
              },
              "& .MuiTabs-flexContainer": { gap: 0.5 },
              "& .MuiTabs-indicator": { height: 3, borderRadius: 999 },
            }}
          >
            <Tab label="TIPOS DE RUBROS" value="5" />
            <Tab label="JUZGADOS" value="6" />
            <Tab label="CAJA DE ABOGADOS" value="2" />
            <Tab label="COLEGIO DE ABOGADOS" value="3" />
            <Tab label="JUS - VALORES" value="1" />
            <Tab label="TASAS DE JUSTICIA" value="4" />
          </TabList>

          <TabPanel value="1" sx={{ p: 3 }}>
            <DateAmountPanel
              icon={<CalculateRoundedIcon />}
              title="Valores JUS"
              subtitle="Histórico del valor JUS utilizado en los cálculos de honorarios"
              fetchFn={getJusValues}
              createFn={createJusValue}
              updateFn={updateJusValue}
              deleteFn={deleteJusValue}
              amountLabel="Valor JUS ($)"
            />
          </TabPanel>
          <TabPanel value="2" sx={{ p: 3 }}>
            <DateAmountPanel
              icon={<AccountBalanceWalletRoundedIcon />}
              title="Caja de Abogados"
              subtitle="Valores históricos de la cuota de la Caja de Abogados"
              fetchFn={getBarAssocValues}
              createFn={createBarAssocValue}
              updateFn={updateBarAssocValue}
              deleteFn={deleteBarAssocValue}
              amountLabel="Cuota Caja ($)"
            />
          </TabPanel>
          <TabPanel value="3" sx={{ p: 3 }}>
            <DateAmountPanel
              icon={<AccountBalanceRoundedIcon />}
              title="Colegio de Abogados"
              subtitle="Valores históricos de la cuota del Colegio de Abogados"
              fetchFn={getBarCollegeFees}
              createFn={createBarCollegeFee}
              updateFn={updateBarCollegeFee}
              deleteFn={deleteBarCollegeFee}
              amountLabel="Cuota Colegio ($)"
            />
          </TabPanel>
          <TabPanel value="4" sx={{ p: 3 }}>
            <TaxRatesPanel />
          </TabPanel>
          <TabPanel value="5" sx={{ p: 3 }}>
            <ItemTypesPanel />
          </TabPanel>
          <TabPanel value="6" sx={{ p: 3 }}>
            <DependenciesPanel />
          </TabPanel>
        </TabContext>
      </GlassCard>
    </Container>
  );
}
